import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

export function TaskFormDialog({
  open, onOpenChange, formData, setFormData, onSubmit, formLoading,
  projects, staffMembers, isEdit,
}) {
  const toggleAssignee = (userId) => {
    if (formData.assignees.includes(userId)) {
      setFormData({ ...formData, assignees: formData.assignees.filter(id => id !== userId) });
    } else {
      setFormData({ ...formData, assignees: [...formData.assignees, userId] });
    }
  };

  const prefix = isEdit ? 'edit' : 'new';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update task details and assignees' : 'Add a task to a project and assign team members'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="form-label">Task Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Review floor plans"
                required
                data-testid={`${prefix}-task-title-input`}
              />
            </div>
            {!isEdit && (
              <div className="space-y-2">
                <Label className="form-label">Project</Label>
                <Select
                  value={formData.project_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid={`${prefix}-task-project-select`}>
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a project</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="form-label">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task details..."
                rows={2}
                data-testid={`${prefix}-task-description-input`}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="form-label">Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  data-testid={`${prefix}-task-due-date-input`}
                />
              </div>
              <div className="space-y-2">
                <Label className="form-label">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid={`${prefix}-task-priority-select`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="form-label">Assign To (Select Multiple)</Label>
              <div className="border border-border rounded-md max-h-48 overflow-y-auto">
                {staffMembers.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">
                    No team members available
                  </p>
                ) : (
                  staffMembers.map(member => (
                    <div
                      key={member.user_id}
                      className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`assignee-${member.user_id}`}
                        checked={formData.assignees.includes(member.user_id)}
                        onCheckedChange={() => toggleAssignee(member.user_id)}
                      />
                      <Label
                        htmlFor={`assignee-${member.user_id}`}
                        className="flex-1 cursor-pointer text-sm font-normal"
                      >
                        {member.name}
                        <span className="text-muted-foreground ml-2">({member.role})</span>
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {formData.assignees.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.assignees.length} team member(s) selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn-primary"
              disabled={formLoading || (!isEdit && !formData.project_id)}
              data-testid={isEdit ? 'edit-task-submit-btn' : 'create-task-submit-btn'}
            >
              {formLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEdit ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                isEdit ? 'Save Changes' : 'Create Task'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
